import { useState } from "react";
import { partyTypes, partyRoles, councils, specialties, partyStatus } from "@/mocks/partes";

interface NewPartyModalProps {
  onClose: () => void;
  onSubmit: (data: Record<string, string>) => void;
}

export default function NewPartyModal({ onClose, onSubmit }: NewPartyModalProps) {
  const [form, setForm] = useState<Record<string, string>>({
    type: "", name: "", tradeName: "", documentId: "", email: "", phone: "",
    address: "", municipality: "Goiânia", uf: "GO", institution: "",
    primaryRole: "", registrationNumber: "", council: "", specialty: "",
    notes: "", initialStatus: "Ativa",
  });

  const update = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
    onClose();
  };

  const isPerson = form.type === "Pessoa física";
  const isPJ = form.type === "Pessoa jurídica" || form.type === "Loteadora" || form.type === "Consultoria ambiental" || form.type === "Empresa executora" || form.type === "Condomínio";
  const isTech = form.primaryRole === "Responsável técnico";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <h3 className="text-lg font-semibold text-foreground-900 font-heading">Nova Parte</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-50 transition-colors">
            <i className="ri-close-line w-full h-full flex items-center justify-center text-xl" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Tipo de parte *</label>
            <select required value={form.type} onChange={(e) => update("type", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900">
              <option value="">Selecione...</option>
              {partyTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {(isPJ || form.type === "Condomínio") ? (
            <>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Razão social *</label>
                <input required type="text" value={form.name} onChange={(e) => update("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Nome fantasia</label>
                <input type="text" value={form.tradeName} onChange={(e) => update("tradeName", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">CNPJ</label>
                <input type="text" value={form.documentId} onChange={(e) => update("documentId", e.target.value)} placeholder="XX.XXX.XXX/0001-XX" className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 placeholder:text-foreground-400" />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Nome *</label>
                <input required type="text" value={form.name} onChange={(e) => update("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">CPF</label>
                <input type="text" value={form.documentId} onChange={(e) => update("documentId", e.target.value)} placeholder="XXX.XXX.XXX-XX" className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 placeholder:text-foreground-400" />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">E-mail</label>
              <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 placeholder:text-foreground-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Telefone</label>
              <input type="text" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 placeholder:text-foreground-400" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Papel principal</label>
            <select value={form.primaryRole} onChange={(e) => update("primaryRole", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900">
              <option value="">Selecione...</option>
              {partyRoles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Instituição vinculada</label>
            <input type="text" value={form.institution} onChange={(e) => update("institution", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 placeholder:text-foreground-400" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Município</label>
              <input type="text" value={form.municipality} onChange={(e) => update("municipality", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">UF</label>
              <input type="text" value={form.uf} onChange={(e) => update("uf", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900" />
            </div>
          </div>

          {isTech && (
            <div className="bg-secondary-50 rounded-xl p-3 space-y-3">
              <p className="text-xs font-semibold text-secondary-700">Registro profissional</p>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Conselho</label>
                <select value={form.council} onChange={(e) => update("council", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900">
                  <option value="">Selecione...</option>
                  {councils.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Número do registro</label>
                <input type="text" value={form.registrationNumber} onChange={(e) => update("registrationNumber", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Especialidade</label>
                <select value={form.specialty} onChange={(e) => update("specialty", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900">
                  <option value="">Selecione...</option>
                  {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Status inicial</label>
            <select value={form.initialStatus} onChange={(e) => update("initialStatus", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900">
              {partyStatus.slice(0, 3).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
            <textarea rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 resize-none" />
          </div>
        </form>
        <div className="border-t border-background-200 px-6 py-4 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">Cancelar</button>
          <button type="submit" onClick={handleSubmit} className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-save-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}